<template>
  <div class="bg-card bg-white border-card-border md:px-6">
    <div class="w-full pt-3">
      <div>
        <h1 slot="header" class="text-xl font-semibold pb-2 mt-5">Link your bank account</h1>
        <h3 slot="header" class="pb-3 border-b mb-4">
          Connect your main direct deposit account to assist in verifying your income and allow VIVA
          to securely transfer funds directly to you
        </h3>
      </div>
    </div>
    <div v-if="isPlaidAccount">
      <div class="flex justify-center my-10">
        <img
          :src="require('@/assets/img/sketches/wallet.svg')"
          alt="LinkBankAccount"
          class="items-center md:block sm:w-1/3 md:w-1/3 py-3"
        />
      </div>
      <template v-if="fundingSourcesExist">
        <div class="pt-5">
          <h2 class="text-lg font-black mb-3">Select Funding Source</h2>
          <div class="">
            <Dropdown
              v-model="selectedFundingSource"
              v-bind:dropdownOptions="fundingSourcesList"
              :value="selectedFundingSource"
              placeholder="semimonthly_15_eom"
              @input="fundingSourceSelected"
              name="Funding Source"
              type="text"
              class="mb-4 w-1/2 px-2"
            />
          </div>
        </div>
      </template>
      <div
        v-if="fundingSourcesExist && selectedFundingSource.length > 0 && !newFundingSourceSelected"
        class="flex items-center my-2 flex-row-reverse px-2 w-full"
      >
        <base-button
          type="primary"
          @click="usePreviousFundingSource"
          class="mt-1"
          :disabled="globalErrorMessage.length > 0"
          :loading="selectedFundingSourceLoading"
          >Continue with selected account</base-button
        >
      </div>
      <div name="fade" class="mt-5">
        <template v-if="newFundingSourceSelected || !fundingSourcesExist">
          <div class="flex flex-wrap md:flex-no-wrap justify-center gap-1 lg:gap-10 px-1 lg:px-16">
            <div class="w-full md:w-1/2 max-w-500 bank-verification-card">
              <div class="text-gray-800 font-bold mt-3 text-justify">
                Use Plaid to sign in to your bank to instantly verify your bank account.
              </div>
              <div class="flex justify-center">
                <base-button
                  type=""
                  @click="linkPlaid()"
                  class="w-full text-sm md:text-base mt-5 btn base-button btn-primary ml-auto"
                  :loading="plaidLoading"
                  :disabled="globalErrorMessage.length > 0"
                  >Instant Verification</base-button
                >
              </div>
            </div>
            <div class="w-full md:w-1/2 max-w-500 bank-verification-card">
              <div class="text-gray-800 font-bold mt-3 text-justify">
                Use your bank's routing and account number. Verification can take up to 3 business
                days.
              </div>
              <div class="flex justify-center">
                <base-button
                  type=""
                  @click="addMdAccount()"
                  class="w-full text-sm md:text-base mt-5 btn base-button btn-primary ml-auto"
                  :loading="manualLoading"
                  :disabled="mdDisabled"
                  >Manual Verification</base-button
                >
              </div>
            </div>
          </div>

          <transition name="in--up">
            <div
              class="flex items-center justify-center w-full px-4"
              v-if="globalErrorMessage.length > 0"
            >
              <h3 class="text-red-500 text-sm">{{ globalErrorMessage }}</h3>
            </div>
          </transition>
          <div class="flex w-full mt-4">
            <button
              class="hover:text-primary mr-auto focus:outline-none rounded"
              type="btn"
              aria-label="navigate back"
              v-on:click="handleBack()"
            >
              <i
                class="fas fa-arrow-left bg-gray-300 rounded-full flex items-center text-lg justify-center w-8 h-8"
              ></i>
            </button>
          </div>
        </template>
      </div>
    </div>

    <div v-else>
      <p>
        To verify your account manually, please enter the following information about the account
        where you receive your paycheck. Two micro-deposits of less than $0.10 will be sent to your
        bank account and will arrive in 1-3 business days. When you receive these micro-deposits,
        please return to this application and provide the values of the deposits.
      </p>
      <form autocomplete="on" class="w-full pt-3 mt-4" novalidate v-on:submit.prevent>
        <div>
          <Input
            v-for="item in fieldOrder"
            v-bind:key="item"
            v-model="accountInformation[item].value"
            :error="accountInformation[item].error"
            :maxlength="accountInformation[item].maxlength"
            :name="accountInformation[item].name"
            :placeholder="accountInformation[item].placeholder"
            :p="accountInformation[item].p"
            :underlined="true"
            class="mb-3"
          />

          <Dropdown
            v-model="accountInformation.accountType.value"
            v-bind:dropdownOptions="['Checking', 'Savings']"
            :error="accountInformation.accountType.error"
            :placeholder="accountInformation.accountType.placeholder"
            :name="accountInformation.accountType.name"
            type="text"
            :underlined="true"
            class="mb-3"
          />
        </div>
      </form>
      <div class="flex w-full mt-4">
        <button
          class="hover:text-primary mr-auto focus:outline-none rounded"
          type="btn"
          aria-label="navigate back"
          v-on:click="handleBack()"
        >
          <i
            class="fas fa-arrow-left bg-gray-300 rounded-full flex items-center text-lg justify-center w-8 h-8"
          ></i>
        </button>

        <base-button
          type="primary ml-auto"
          :disabled="!nextEnabled"
          :loading="loading"
          aria-label="navigate next"
          @click="linkMD()"
        >
          Link Account Manually
        </base-button>
      </div>
      <transition name="in--up">
        <div
          class="flex items-center justify-center w-full px-4"
          v-if="globalErrorMessage.length > 0"
        >
          <h3 class="text-red-500 text-sm">{{ globalErrorMessage }}</h3>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import Dropdown from '@/components/VIVA/Dropdown.vue'
import Engine from '@/mixins/Engine'
import { mapGetters } from 'vuex'
import Swal from 'sweetalert2'

// import Input from '../VIVA/Input.vue'
export default {
  name: 'FundingAccount',
  mixins: [Engine],
  components: {
    Dropdown
    // Input
  },
  data: function () {
    return {
      selectedFundingSourceLoading: false,
      selectedFundingSource: '',
      selectedFundingSourceObject: '',
      accountInformation: {
        routingNumber: {
          name: 'Routing Number',
          placeholder: '123456789',
          p: 'The first number printed on the bottom of your checks (9 digits).',
          maxlength: 9,
          value: '',
          error: ''
        },
        accountNumber: {
          name: 'Account Number',
          placeholder: '1234567890',
          p: 'The second number printed on the bottom of your checks (4-17 digits).',
          maxlength: 17,
          value: '',
          error: ''
        },
        bankName: {
          name: 'Institution Name',
          placeholder: 'Bank of America',
          p: 'The name of the bank.',
          maxlength: 50,
          value: '',
          error: ''
        },
        accountType: {
          name: 'Account Type',
          placeholder: 'Checking',
          p: 'The type of bank account.',
          value: '',
          error: ''
        }
      },
      fieldOrder: ['routingNumber', 'accountNumber', 'bankName'],
      exitedPlaid: false,
      loading: false,
      firstClick: true,
      globalErrorMessage: '',
      plaidLoading: false,
      manualLoading: false,
      newAccountSelected: false
    }
  },
  computed: {
    fundingSourcesList() {
      /**
       * Extra check to filter out Viridian accounts
       */
      let sources = this.fundingSources.filter((source) => {
        const { evolve } = source
        return evolve
      })
      sources = sources.map(function (source) {
        return source.name
      })
      sources.push('New Account')
      return sources
    },
    newFundingSourceSelected() {
      return this.selectedFundingSource === 'New Account'
    },
    fundingSourcesExist() {
      return this.fundingSources.length > 0
    },
    ...mapGetters(['fundingSources']),
    isPlaidAccount() {
      return !this.exitedPlaid
    },
    routingNumber() {
      return this.accountInformation.routingNumber.value
    },
    accountNumber() {
      return this.accountInformation.accountNumber.value
    },
    bankName() {
      return this.accountInformation.bankName.value
    },
    accountType() {
      return this.accountInformation.accountType.value
    },
    accountInformationValues() {
      return Object.keys(this.accountInformation).map((k) => this.accountInformation[k].value)
    },
    fieldsValid() {
      const errorsExist = Object.keys(this.accountInformation).filter(
        (fn) => this.accountInformation[fn].error.length > 0
      )
      const emptyRequired = Object.keys(this.accountInformation).filter(
        (fn) =>
          !this.accountInformation[fn].optional && this.accountInformation[fn].value.length === 0
      )
      return errorsExist.length + emptyRequired.length === 0
    },
    nextEnabled() {
      return this.firstClick || this.fieldsValid
    },
    mdDisabled() {
      const now = new Date()
      const deadline = new Date('2021-12-20')
      return this.globalErrorMessage.length > 0 || now < deadline
    }
  },
  mounted() {
    if (this.fundingSourcesExist) {
      Swal.fire({
        title: 'Previous Accounts',
        confirmButtonColor: '#69D393',
        text: 'We noticed that you have previous bank accounts on file. You can select previous funding accounts or add new ones.',
        icon: 'info'
      })
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  },
  methods: {
    usePreviousFundingSource() {
      this.selectedFundingSourceLoading = true
      const accountData = {
        fs_account_id: this.selectedFundingSourceObject.account_id
      }
      this.$store.commit(
        'applicationData/setActiveFundingSource',
        this.selectedFundingSourceObject.account_id
      )
      this.$store
        .dispatch('applicationData/uploadActiveFundingSource', this.selectedFundingSourceObject)
        .then(() => {
          this.handleNext()
        })
        .catch((e) => {
          console.log(e)
          console.log(`FE Error in usePreviousFundingSource: ${e}`)
        })
        .finally(() => {
          this.selectedFundingSourceLoading = false
        })
    },
    fundingSourceSelected() {
      this.selectedFundingSourceObject = this.fundingSources.filter((source) => {
        return this.selectedFundingSource === source.name
      })[0]
    },
    addMdAccount() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      this.exitedPlaid = true
      this.loading = false
    },
    linkPlaid() {
      this.loading = true
      this.plaidLoading = true
      this.$store
        .dispatch('applicationData/linkPlaid')
        .then((outcome) => {
          console.log('Outcome in fundingAccount ', outcome)
          this.loading = false
          this.plaidLoading = false
          if (outcome != 'Exited') {
            // Successful link
            this.handleNext()
          }
        })
        .catch((e) => {
          this.loading = false
          this.plaidLoading = false
          if (e.message.toLowerCase().includes('bank already exists')) {
            Swal.fire({
              title: 'Account Exists',
              confirmButtonColor: '#69D393',
              text: 'You have previously linked this bank account. Please go back and select that account or add a new account.',
              icon: 'info'
            })
          } else if (
            e.message.toLowerCase().includes('the maximum number of banks have been added')
          ) {
            Swal.fire({
              title: 'Pending Account Verification',
              confirmButtonColor: '#69D393',
              text: 'You have an account pending micro-deposits verification outstanding.',
              icon: 'info'
            })
          }
          console.log(e)
          console.log(`FE Error in linkPlaid: ${e}`)
        })
    },
    linkMD() {
      if (this.firstClick) {
        this.firstClick = false
        const emptyRequired = Object.keys(this.accountInformation).filter(
          (fieldName) =>
            !this.accountInformation[fieldName].optional &&
            this.accountInformation[fieldName].value.length === 0
        )
        const existingErrors = Object.keys(this.accountInformation).filter(
          (fieldName) => this.accountInformation[fieldName].error.length > 0
        )
        if (emptyRequired.length + existingErrors.length > 0) {
          console.log(emptyRequired)
          emptyRequired.forEach((requiredField) => {
            this.accountInformation[requiredField].error = 'This is a required field.'
          })
          this.globalErrorMessage = 'Please fix all errors before proceeding.'
          return
        }
      }
      this.loading = true
      const outputData = {
        name: this.bankName,
        account: this.accountNumber,
        routing: this.routingNumber,
        type: this.accountType
      }
      let self = this
      this.$store
        .dispatch('applicationData/initializeMicroDeposits', outputData)
        .then(() => this.handleNext())
        .catch((e) => {
          console.log(e)
          console.log(`FE Error in linkMD: ${e}`)

          if (e.message.toLowerCase().includes('routing')) {
            this.accountInformation.routingNumber.error =
              'Please check that you entered the correct routing number.'
            this.globalErrorMessage = 'Please fix all errors before proceeding.'
          } else if (e.message.toLowerCase().includes('account')) {
            this.accountInformation.routingNumber.error =
              'Please check that you entered the correct account number.'
            this.globalErrorMessage = 'Please fix all errors before proceeding.'
          } else {
            this.globalErrorMessage = `Unexpected error occurred, please refresh. Error: ${e.message}`
            if (e.message.toLowerCase().includes('micro deposits already')) {
              Swal.fire({
                title: 'Account Exists',
                confirmButtonColor: '#69D393',
                text: 'You have used this account previously. Please go back and select that account or add a new account.',
                icon: 'info'
              }).then(function () {
                self.handleBack()
              })
            }
            if (e.message.toLowerCase().includes('bank already exists')) {
              Swal.fire({
                title: 'Account Exists',
                confirmButtonColor: '#69D393',
                text: 'You have previously linked this bank account. Please go back and select that account or add a new account.',
                icon: 'info'
              }).then(function () {
                self.handleBack()
              })
            }
          }
        })
        .finally(() => (this.loading = false))
    },
    handleNext() {
      this.$emit('next')
    },
    handleBack() {
      this.globalErrorMessage = ''
      window.scrollTo({ top: 0, behavior: 'smooth' })
      if (this.exitedPlaid) {
        this.exitedPlaid = false
      } else {
        this.$emit('back')
      }
    }
  },
  watch: {
    accountInformationValues: {
      handler(v) {
        this.globalErrorMessage = ''
      }
    },
    isPlaidAccount: {
      handler() {
        Object.keys(this.accountInformation).forEach((fieldName) => {
          this.accountInformation[fieldName].optional = !this.exitedPlaid
        })
      }
    },
    routingNumber: {
      handler(v) {
        if (v.length != 9) {
          this.accountInformation.routingNumber.error = 'Please enter a valid routing number'
        } else {
          let blockedRoutingNumbers = [
            '031101169',
            '263181449',
            '301078854',
            '314985578',
            '273975292',
            '271176909',
            '122402133',
            '311978708',
            '231176664',
            '111926057',
            '075917898',
            '084203276',
            '253978769',
            '271081599',
            '291378897',
            '075072199',
            '122242597',
            '051409605',
            '114925275',
            '265273096',
            '071004200',
            '124385119',
            '221581641',
            '061020948',
            '091302720',
            '111105441',
            '066012333',
            '084203603',
            '321380085',
            '021214891',
            '283079117',
            '122239982',
            '291471024',
            '303985932',
            '091303855',
            '101115302',
            '211180023',
            '043000261',
            '242071033',
            '284084101',
            '272482223',
            '096017670',
            '104102095',
            '313084881',
            '091017400',
            '081916058',
            '271176200',
            '031207908',
            '275080567',
            '084003997',
            '124084834',
            '312270379',
            '101115328',
            '067011294',
            '271982119',
            '064209371',
            '121139313',
            '021584181',
            '211287913',
            '311376818',
            '324079393',
            '051000101',
            '302284090',
            '314990165',
            '071993159',
            '031203999',
            '091409843',
            '031100144',
            '062202985',
            '311175860',
            '031100393',
            '321371528',
            '124071889',
            '101089742',
            '124303162',
            '063192874',
            '124303120',
            '031101279',
            '103100195',
            '071922557',
            '091302966',
            '021409169',
            '064206594',
            '053012838',
            '091017138',
            '122287675',
            '073972181',
            '026014902',
            '071025797',
            '028000121',
            '084106768',
            '073923033',
            '062202150',
            '065402892',
            '053111632',
            '031902766',
            '111926112',
            '041215663',
            '026014928',
            '031919225',
            '042287027',
            '101019084',
            '114926009',
            '026014892',
            '061120835',
            '026073150',
            '124085244',
            '324172766',
            '101019521',
            '124303201',
            '103113315',
            '031302777',
            '103113357'
          ]
          if (blockedRoutingNumbers.includes(v)) {
            this.accountInformation.routingNumber.error = `Your financial institution with routing number ${v} is not supported by our payment processor. Please link an account from a different institution to continue your application.`
          } else {
            this.accountInformation.routingNumber.error = ''
          }
        }
      }
    },
    accountNumber: {
      handler(v) {
        if (v.length < 4 || v.length > 17) {
          this.accountInformation.accountNumber.error = 'Please enter a valid bank account number'
        } else {
          this.accountInformation.accountNumber.error = ''
        }
      }
    },
    bankName: {
      handler(v) {
        if (v.length < 1 || v.length > 50) {
          this.accountInformation.bankName.error = 'Please enter a valid bank name'
        } else {
          this.accountInformation.bankName.error = ''
        }
      }
    },
    accountType: {
      handler(v) {
        if (v.length < 1) {
          this.accountInformation.accountType.error = 'Please choose the account type'
        } else {
          this.accountInformation.accountType.error = ''
        }
      }
    }
  }
}
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
